import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { SyncOutlined, WarningAmber, HighlightOff, CheckCircleOutline } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';

import { FHButton, FHConfirmDialog } from '../../../base';
import {
  behaviorActions,
  goalActions,
  maladaptiveBehaviorActions,
  treatmentPlanActions,
} from '../../../../store';
import GoalsContainerV1 from './v1/GoalsContainer';
import GoalsContainerV2 from './v2/GoalsContainer';
import OldGoals from './old';
import UnsavedIndicator from '../components/unsavedIndicator';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { getValidationErrorCount } from './common/utils/general';
import { shouldUnlinkSAGGoal, willV2GoalBeActive } from './v2/utils/general';
import { FORTA_WHITE, FORTA_BLUE, FORTA_ERROR } from '../../../../utils/colors';
import { Roles, Themes } from '../../../../constants';
import { BAYS } from '../../../../constants';
import { areUserRolesIn } from '../../../../helpers';
import { TreatmentPlanStatus } from '../constants/general';
import { GoalTypes, TreatmentPlanResourceVersion } from './common/constants/general';

import type { ITreatmentPlanGoal } from '../../../../utils/types';
import type { RootState } from '../../../../store';

const tabButtonStyle = (isActive?: boolean) => {
  return {
    mr: 3,
    borderRadius: '20px',
    backgroundColor: isActive ? FORTA_BLUE.primary : 'transparent',
    border: `2px solid ${isActive ? FORTA_BLUE.primary : FORTA_BLUE.light2}`,
    color: isActive ? FORTA_WHITE : FORTA_BLUE.primary,

    '&:hover': {
      borderColor: FORTA_BLUE.primary,
    },
  };
};

function Goals() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isTpEditable = useTreatmentPlanEditable();

  const { user } = useSelector((state: RootState) => state.auth);
  const { current: treatmentPlan, tpStatusIdByLabel } = useSelector(
    (state: RootState) => state.treatmentPlans,
  );
  const {
    treatmentPlanId: currentTreatmentPlanId,
    treatmentPlanStatusId: currentTreatmentPlanStatusId,
    behaviorReductionGoals,
    skillAcquisitionGoals,
    caregiverGoals,
    validationErrors,
  } = useSelector((state: RootState) => state.goals);

  const tpBay = treatmentPlan?.bay ?? BAYS.BAY_2_6;
  const goalVersion = treatmentPlan?.resource_versions?.goal;
  const targetVersion = treatmentPlan?.resource_versions?.target;
  const isResourceVersionV1 =
    goalVersion === TreatmentPlanResourceVersion.V1 ||
    targetVersion === TreatmentPlanResourceVersion.V1;
  const isResourceVersionV2 =
    goalVersion === TreatmentPlanResourceVersion.V2 ||
    targetVersion === TreatmentPlanResourceVersion.V2;

  const [goalType, setGoalType] = useState<GoalTypes>(GoalTypes.SkillAcquisition);
  const [isBRGoalDirty, setIsBRGoalDirty] = useState<boolean>(false);
  const [isSAGoalDirty, setIsSAGoalDirty] = useState<boolean>(false);
  const [isPCGoalDirty, setIsPCGoalDirty] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [showRevertDialog, setShowRevertDialog] = useState<boolean>(false);
  const [unlinkedActiveBRGGoalCount, setUnlinkedActiveBRGGoalCount] = useState<number>(0);
  const [linkSAGGoalErrorCount, setLinkedSAGGoalErrorCount] = useState<number>(0);
  const [linkedInactiveSAGGoalErrorCount, setLinkedInactiveSAGGoalErrorCount] = useState<number>(0);

  const [goalCount, targetCount] = useMemo(() => {
    const goalCount =
      (behaviorReductionGoals?.length ?? 0) +
      (skillAcquisitionGoals?.length ?? 0) +
      (caregiverGoals?.length ?? 0);
    const targetCount =
      (behaviorReductionGoals?.reduce((acc, goal) => acc + goal.targets.length, 0) ?? 0) +
      (skillAcquisitionGoals?.reduce((acc, goal) => acc + goal.targets.length, 0) ?? 0) +
      (caregiverGoals?.reduce((acc, goal) => acc + goal.targets.length, 0) ?? 0);
    return [goalCount, targetCount];
  }, [behaviorReductionGoals, skillAcquisitionGoals, caregiverGoals]);

  const unsavedSAGCount = useMemo(() => {
    return (
      skillAcquisitionGoals?.reduce((acc, goal) => {
        let count = 0;
        if (goal?.dirty && goal?.is_editing) {
          count++;
        }
        goal?.targets?.forEach((target) => {
          if (target.dirty && target.is_editing) {
            count++;
          }
        });
        return acc + count;
      }, 0) ?? 0
    );
  }, [skillAcquisitionGoals]);

  const unsavedBRGCount = useMemo(() => {
    let brgGoalNotLinkedCount = 0;
    let linkedSAGGoalErrorCount = 0;
    let linkedInactiveSagErrorCount = 0;

    // Calculate num of active BRGs not linked with SAGs
    behaviorReductionGoals.forEach((goal) => {
      const isActive = willV2GoalBeActive(goal);
      if (isActive && !goal?.linked_goals?.skill_acquisition?.length) {
        brgGoalNotLinkedCount++;
      }
    });

    setUnlinkedActiveBRGGoalCount(brgGoalNotLinkedCount);

    // Calculate num of inactive BRGs linked with SAGs
    behaviorReductionGoals.forEach((goal) => {
      const shouldUnlink = shouldUnlinkSAGGoal(goal);
      if (shouldUnlink && goal?.linked_goals?.skill_acquisition?.length) {
        linkedSAGGoalErrorCount++;
      }
    });

    setLinkedSAGGoalErrorCount(linkedSAGGoalErrorCount);

    // Calculate num of inactive SAGs linked with GRGs
    behaviorReductionGoals.forEach((goal) => {
      if (goal?.linked_goals?.skill_acquisition?.length) {
        goal?.linked_goals?.skill_acquisition?.forEach((g) => {
          const sagGoal = skillAcquisitionGoals.find((sg) => sg.id === g.id);
          if (sagGoal) {
            const isSagActive = willV2GoalBeActive(sagGoal);
            if (!isSagActive) {
              linkedInactiveSagErrorCount++;
            }
          }
        });
      }
    });

    setLinkedInactiveSAGGoalErrorCount(linkedInactiveSagErrorCount);

    return (
      behaviorReductionGoals?.reduce((acc, goal) => {
        let count = 0;
        if (goal?.dirty && goal?.is_editing) {
          count++;
        }
        goal?.targets?.forEach((target) => {
          if (target.dirty && target.is_editing) {
            count++;
          }
        });
        return acc + count;
      }, 0) ?? 0
    );
  }, [behaviorReductionGoals, skillAcquisitionGoals]);

  const unsavedPCGCount = useMemo(() => {
    return (
      caregiverGoals?.reduce((acc, goal) => {
        let count = 0;
        if (goal?.dirty && goal?.is_editing) {
          count++;
        }
        goal?.targets?.forEach((target) => {
          if (target.dirty && target.is_editing) {
            count++;
          }
        });
        return acc + count;
      }, 0) ?? 0
    );
  }, [caregiverGoals]);

  useEffect(() => {
    if (tpBay !== BAYS.BAY_2_5) return;
    setGoalType(GoalTypes.Caregiver);
  }, [tpBay]);

  useEffect(() => {
    // Tp independent call
    if (isTpEditable) {
      dispatch(behaviorActions.getList({ getAccessTokenSilently }));
    }
  }, [isTpEditable]);

  useEffect(() => {
    if (!treatmentPlan) return;

    // Always update status ID first
    dispatch(
      goalActions.updateState({
        key: 'treatmentPlanStatusId',
        value: treatmentPlan.status_id,
      }),
    );

    // To persist local state data
    if (currentTreatmentPlanId === treatmentPlan.id) return;

    dispatch(
      maladaptiveBehaviorActions.getMaladaptiveBehavior({
        planId: treatmentPlan.id,
        getAccessTokenSilently,
      }),
    );
    dispatch(
      goalActions.updateState({
        key: 'treatmentPlanId',
        value: treatmentPlan.id,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlan]);

  useEffect(() => {
    if (!treatmentPlan) return;
    if (
      currentTreatmentPlanId === treatmentPlan.id &&
      currentTreatmentPlanStatusId === treatmentPlan.status_id
    )
      return;

    dispatch(
      goalActions.getGoals({
        treatmentPlanId: treatmentPlan.id,
        include_pending:
          areUserRolesIn(user?.roles, [Roles.Vbt]) &&
          !areUserRolesIn(user?.roles, [Roles.AssistantAssessor])
            ? false
            : true,
        getAccessTokenSilently,
      }),
    );
  }, [treatmentPlan, currentTreatmentPlanId, currentTreatmentPlanStatusId]);

  useEffect(() => {
    if (!behaviorReductionGoals) return;

    const filteredBRGoals =
      behaviorReductionGoals?.filter((goal: ITreatmentPlanGoal) => goal.is_active) || [];
    setIsBRGoalDirty(
      filteredBRGoals.some((goal) => goal?.dirty || goal.targets?.some((target) => target?.dirty)),
    );

    const filteredSAGoals =
      skillAcquisitionGoals?.filter((goal: ITreatmentPlanGoal) => goal.is_active) || [];
    setIsSAGoalDirty(
      filteredSAGoals.some((goal) => goal?.dirty || goal.targets?.some((target) => target?.dirty)),
    );

    const filteredPCGoals =
      caregiverGoals?.filter((goal: ITreatmentPlanGoal) => goal.is_active) || [];
    setIsPCGoalDirty(
      filteredPCGoals.some((goal) => goal?.dirty || goal.targets?.some((target) => target?.dirty)),
    );
  }, [behaviorReductionGoals, skillAcquisitionGoals, caregiverGoals]);

  const handleMigrateTreatmentPlan = async () => {
    setShowConfirmDialog(false);
    const res = await dispatch(
      treatmentPlanActions.migrateTreatmentPlan({
        treatmentPlanId: treatmentPlan?.id,
        data: {
          goal: TreatmentPlanResourceVersion.V2,
          target: TreatmentPlanResourceVersion.V2,
        },
        getAccessTokenSilently,
      }),
    );

    if (!res.error) {
      dispatch(
        goalActions.getGoals({
          treatmentPlanId: treatmentPlan?.id,
          include_pending:
            areUserRolesIn(user?.roles, [Roles.Vbt]) &&
            !areUserRolesIn(user?.roles, [Roles.AssistantAssessor])
              ? false
              : true,
          getAccessTokenSilently,
        }),
      );
    }
  };

  const handleRevertTreatmentPlan = async () => {
    setShowRevertDialog(false);
    const res = await dispatch(
      treatmentPlanActions.migrateTreatmentPlan({
        treatmentPlanId: treatmentPlan?.id,
        data: {
          goal: TreatmentPlanResourceVersion.V1,
          target: TreatmentPlanResourceVersion.V1,
        },
        getAccessTokenSilently,
      }),
    );
    if (res.error) return;

    dispatch(
      goalActions.getGoals({
        treatmentPlanId: treatmentPlan?.id,
        getAccessTokenSilently,
      }),
    );
  };

  // Render old goal UI for read only TP
  if (treatmentPlan && treatmentPlan.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Readonly])
    return <OldGoals />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', py: 1 }}>
      {areUserRolesIn(user?.roles, [Roles.Bcba]) &&
        isResourceVersionV1 &&
        (treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Editing] ||
          treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Started]) && (
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, mb: 1 }}>
            <FHButton startIcon={<SyncOutlined />} onClick={() => setShowConfirmDialog(true)}>
              Convert to new format
            </FHButton>
          </Box>
        )}

      {areUserRolesIn(user?.roles, [Roles.Admin]) &&
        isResourceVersionV2 &&
        (treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Editing] ||
          treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Started]) && (
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, mb: 1 }}>
            <FHButton startIcon={<SyncOutlined />} onClick={() => setShowRevertDialog(true)}>
              Revert to old format
            </FHButton>
          </Box>
        )}

      <Box sx={{ display: 'flex', flexDirection: 'column', py: 2 }}>
        <Box sx={{ display: 'flex', mb: 4 }}>
          {tpBay !== BAYS.BAY_2_5 && (
            <>
              <Box sx={{ position: 'relative' }}>
                {unsavedSAGCount > 0 && (
                  <UnsavedIndicator
                    right="45px"
                    top="-10px"
                    fontSize={12}
                    sx={{ borderRadius: '50%', width: 24, height: 24, p: 0 }}
                    title={`${unsavedSAGCount}`}
                  />
                )}
                <FHButton
                  data-testid="skillAcquisitionGoalsTab"
                  colorTheme={Themes.Primary}
                  sx={{ ...tabButtonStyle(goalType === GoalTypes.SkillAcquisition) }}
                  onClick={() => setGoalType(GoalTypes.SkillAcquisition)}
                  //Display error count for specific goal type only if the goal type is in V2,
                  //otherwise display error if the goal type is dirty
                  error={
                    treatmentPlan
                      ? treatmentPlan.resource_versions?.goal === TreatmentPlanResourceVersion.V2
                        ? skillAcquisitionGoals?.length === 0 ||
                          getValidationErrorCount(validationErrors, GoalTypes.SkillAcquisition)
                        : skillAcquisitionGoals?.length === 0 || isSAGoalDirty
                      : false
                  }
                >
                  Skill Acquisition Goals
                </FHButton>
              </Box>
              <Box sx={{ position: 'relative' }}>
                {unsavedBRGCount > 0 && (
                  <UnsavedIndicator
                    right="45px"
                    top="-10px"
                    fontSize={12}
                    sx={{ borderRadius: '50%', width: 24, height: 24, p: 0 }}
                    title={`${unsavedBRGCount}`}
                  />
                )}
                <FHButton
                  data-testid="behaviorReductionGoalsTab"
                  colorTheme={Themes.Primary}
                  sx={{ ...tabButtonStyle(goalType === GoalTypes.BehaviorReduction) }}
                  onClick={() => setGoalType(GoalTypes.BehaviorReduction)}
                  //Display error count for specific goal type only if the goal type is in V2,
                  //otherwise display error if the goal type is dirty
                  error={
                    treatmentPlan
                      ? treatmentPlan.resource_versions?.goal === TreatmentPlanResourceVersion.V2
                        ? getValidationErrorCount(validationErrors, GoalTypes.BehaviorReduction) +
                          unlinkedActiveBRGGoalCount +
                          linkSAGGoalErrorCount +
                          linkedInactiveSAGGoalErrorCount
                        : isBRGoalDirty
                      : false
                  }
                >
                  Behavior Reduction Goals
                </FHButton>
              </Box>
            </>
          )}
          <Box sx={{ position: 'relative' }}>
            {unsavedPCGCount > 0 && (
              <UnsavedIndicator
                right="45px"
                top="-10px"
                fontSize={12}
                sx={{ borderRadius: '50%', width: 24, height: 24, p: 0 }}
                title={`${unsavedPCGCount}`}
              />
            )}
            <FHButton
              data-testid="parentCaregiverGoalsTab"
              colorTheme={Themes.Primary}
              sx={{ ...tabButtonStyle(goalType === GoalTypes.Caregiver) }}
              onClick={() => setGoalType(GoalTypes.Caregiver)}
              //Display error count for specific goal type only if the goal type is in V2,
              //otherwise display error if the goal type is dirty
              error={
                treatmentPlan
                  ? treatmentPlan.resource_versions?.goal === TreatmentPlanResourceVersion.V2
                    ? caregiverGoals?.length === 0 ||
                      getValidationErrorCount(validationErrors, GoalTypes.Caregiver)
                    : caregiverGoals?.length === 0 || isPCGoalDirty
                  : false
              }
            >
              Parent/Caregiver Goals
            </FHButton>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              flexGrow: 1,
              mr: 1,
            }}
          >
            <Typography
              color={FORTA_BLUE.primary}
              sx={{
                fontWeight: 500,
                textDecoration: 'underline',
                textAlign: 'center',
                cursor: 'pointer',
              }}
              onClick={() => {
                dispatch(goalActions.updateGoalsExpandState(false));
              }}
            >
              {'Collapse all goals'}
            </Typography>
          </Box>
        </Box>

        {isResourceVersionV1 ? (
          <GoalsContainerV1 type={goalType} goalVersion={TreatmentPlanResourceVersion.V1} />
        ) : (
          <GoalsContainerV2 type={goalType} goalVersion={TreatmentPlanResourceVersion.V2} />
        )}
      </Box>
      {isResourceVersionV1 && (
        <FHConfirmDialog
          dialogStyles={{
            padding: '32px',
            maxHeight: '85vh',
            height: 'auto',
            width: '580px',
            maxWidth: '700px',
          }}
          sx={{
            textAlign: 'left',
            '& .MuiModal-backdrop': {
              backdropFilter: 'blur(2px)',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
          }}
          hideCloseIcon
          open={showConfirmDialog}
          colorTheme={Themes.Primary}
          contentRenderer={() => (
            <Box sx={{ textAlign: 'left', fontSize: 21, padding: '30px 0px 48px 0px' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <WarningAmber sx={{ color: FORTA_ERROR, width: 24, height: 24 }} />
                <Typography sx={{ fontSize: 16, mb: 2 }}>
                  Do you want to convert the current treatment plan&apos;s goals & targets to the
                  new version?
                </Typography>
              </Box>
              {goalCount > 0 ? (
                <>
                  <Typography sx={{ fontSize: 16, ml: 5 }}>
                    The current Treatment Plan has
                  </Typography>
                  <Box sx={{ display: 'flex', mt: 3, ml: 5 }}>
                    <Box
                      sx={{
                        backgroundColor: FORTA_BLUE.light3,
                        borderRadius: '8px',
                        width: 200,
                        height: 134,
                        mr: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography fontSize={48}>{goalCount}</Typography>
                        <Typography mt={1}>Goals</Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: FORTA_BLUE.light3,
                        borderRadius: '8px',
                        width: 200,
                        height: 134,
                        ml: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography fontSize={48}>{targetCount}</Typography>
                        <Typography mt={1}>Targets</Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              ) : null}
              <Typography sx={{ fontSize: 16, mt: 2, ml: 5 }}>
                This action is irreversible and will require you to provide additional data.
              </Typography>
            </Box>
          )}
          cancelStartIcon={<HighlightOff />}
          submitStartIcon={<CheckCircleOutline />}
          submittext={'Convert treatment plan'}
          canceltext={'Cancel'}
          onClose={() => setShowConfirmDialog(false)}
          onSubmit={handleMigrateTreatmentPlan}
        />
      )}
      <FHConfirmDialog
        dialogStyles={{
          padding: '32px',
          maxHeight: '85vh',
          height: 'auto',
          width: '700px',
          maxWidth: '700px',
        }}
        open={showRevertDialog}
        colorTheme={Themes.Primary}
        title={'Revert treatment plan'}
        contentRenderer={() => (
          <Box sx={{ textAlign: 'left', fontSize: 21, padding: '30px 0px 48px 0px' }}>
            <Typography sx={{ fontSize: 21, ml: 1, lineHeight: '28px' }}>
              Are you sure you want to revert this Treatment plan to the old version?
            </Typography>
          </Box>
        )}
        submittext={'Revert treatment plan'}
        canceltext={'Cancel'}
        onClose={() => setShowRevertDialog(false)}
        onSubmit={handleRevertTreatmentPlan}
      />
    </Box>
  );
}

export default Goals;
